import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Timer,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Plus
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const AdminOvertimePage = () => {
  const [overtime, setOvertime] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newOT, setNewOT] = useState({ project_id: '', hours: 2, work_date: new Date().toISOString().split('T')[0], reason: '' });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [otRes, projRes] = await Promise.all([
        axios.get(`${API}/admin/overtime`, { headers }),
        axios.get(`${API}/admin/hr-projects`, { headers })
      ]);
      setOvertime(otRes.data.overtime_requests);
      setProjects(projRes.data.projects);
    } catch (error) {
      if (error.response?.status === 401) navigate("/admin-login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token, navigate]);

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/admin/overtime`, newOT, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Overtime request submitted");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to submit overtime");
    }
  };

  const handleApproval = async (id, action) => {
    try {
      await axios.put(`${API}/admin/overtime/${id}/approve`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Overtime ${action}d`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} overtime`);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center"><div className="text-[#c9a227]">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#0b1c2d] lg:ml-64 p-6" data-testid="admin-overtime-page">
      <div className="mb-6">
        <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />Back to Dashboard</Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Overtime Requests</h1>
            <p className="text-slate-400 mt-1">{overtime.length} requests</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />Request Overtime</Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader><DialogTitle className="text-white">Request Overtime</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Project</Label>
                  <select value={newOT.project_id} onChange={(e) => setNewOT({...newOT, project_id: e.target.value})} className="w-full mt-1 p-2 bg-slate-800 border border-slate-700 rounded-sm text-white">
                    <option value="">Select project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Work Date</Label>
                    <Input type="date" value={newOT.work_date} onChange={(e) => setNewOT({...newOT, work_date: e.target.value})} className="mt-1 bg-slate-800 border-slate-700 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Hours</Label>
                    <Input type="number" min="0.5" max="12" step="0.5" value={newOT.hours} onChange={(e) => setNewOT({...newOT, hours: parseFloat(e.target.value)})} className="mt-1 bg-slate-800 border-slate-700 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Reason</Label>
                  <Textarea value={newOT.reason} onChange={(e) => setNewOT({...newOT, reason: e.target.value})} className="mt-1 bg-slate-800 border-slate-700 text-white" placeholder="Reason for overtime..." />
                </div>
                <Button onClick={handleSubmit} className="w-full btn-primary">Submit Request</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>Employee</th><th>Date</th><th>Hours</th><th>Rate</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {overtime.map((ot) => (
                  <tr key={ot.id}>
                    <td className="text-white">{ot.user_name}</td>
                    <td className="text-slate-400">{ot.work_date}</td>
                    <td className="text-[#c9a227] font-semibold">{ot.hours}h</td>
                    <td className="text-purple-400">{ot.overtime_rate}x</td>
                    <td className="text-slate-400 max-w-[200px] truncate">{ot.reason}</td>
                    <td>
                      <Badge className={ot.status === 'approved' ? 'status-green' : ot.status === 'rejected' ? 'status-red' : 'status-yellow'}>{ot.status}</Badge>
                    </td>
                    <td>
                      {ot.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproval(ot.id, 'approve')} className="bg-green-600 hover:bg-green-700 h-7 px-2"><CheckCircle className="w-4 h-4" /></Button>
                          <Button size="sm" onClick={() => handleApproval(ot.id, 'reject')} className="bg-red-600 hover:bg-red-700 h-7 px-2"><XCircle className="w-4 h-4" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOvertimePage;
